import { Inject, Injectable } from '@angular/core';
import { MessagingService } from '../../cross-team-shared/messaging/messaging.service';
import { BrowserControlService } from '../../cross-team-shared/misc-services/browser-control.service';
import { selectionToArray } from '../../cross-team-shared/misc-utils';
import {
  ContextMenuOption,
  MenuType,
} from '../../team-apis/content/content-connector';
import {
  ShellConnector,
  shellConnectorToken,
} from '../../team-apis/core/shell-connector';
import { RedrawService } from './redraw.service';
import { AbstractSensorContentStore2D } from './state/abstract-sensor-content-store-2d';

@Injectable()
export class ContextMenuOptionsService {
  constructor(
    private browserControlService: BrowserControlService,
    private redrawService: RedrawService,
    @Inject(shellConnectorToken)
    private shellConnector: ShellConnector,
    private store: AbstractSensorContentStore2D,
    private messagingService: MessagingService,
  ) {}

  getContextMenuOptions(menuType: MenuType) {
    if (menuType === 'shape') {
      return this.getShapeContextMenuOptions();
    } else {
      return this.getCanvasContextMenuOptions();
    }
  }

  private async getShapeContextMenuOptions() {
    return [
      {
        label: 'Redraw',
        action: () => this.redrawService.redraw(),
        disabled: !this.redrawService.canRedraw(),
        hidden: this.store.state().readOnly,
        shortcut: 'redraw',
      },
      this.getCopyPositionOption(),
      this.getAddMissingShapeMarkerOption(),
      this.getAddIssueOption(),
    ];
  }

  private async getCanvasContextMenuOptions() {
    const menuOptions: ContextMenuOption[] = [];

    menuOptions.push(this.getCopyPositionOption());
    menuOptions.push(this.getAddMissingShapeMarkerOption());
    menuOptions.push(this.getAddIssueOption());
    return menuOptions;
  }

  private getCopyPositionOption() {
    const coordinates = this.store.state().mouse.imagePoint;
    const outputType = this.store.state().output.type;
    const frameNumber = this.store.state().frameNumber;
    const selection = selectionToArray(this.store.state().selection);

    let text = `[coords: ${coordinates.toString()}`;
    if (outputType === 'VideoOutput') {
      text = text.concat(` | frame: ${frameNumber}`);
    }
    if (selection.length) {
      text = text.concat(` | id: ${selection}`);
    }
    text = text.concat(']');

    return {
      label: 'Copy position',
      action: async () => {
        await this.browserControlService.writeClipboardText(text);
        this.shellConnector.trackEvent({
          id: 'copy_position',
          bucket: 'quality_feedback',
          coordinates,
        });
        this.messagingService.show('Copied');
      },
      shortcut: '',
    };
  }

  private getAddMissingShapeMarkerOption(): ContextMenuOption {
    const point = this.store.state().mouse.imagePoint;
    return {
      label: 'Add missing shape marker',
      hidden: !this.shouldShowAddMissingShapeMarker(),
      action: () => {
        this.shellConnector.addMissingShapeFeedback(point);
      },
    };
  }

  private shouldShowAddMissingShapeMarker() {
    return this.store.state().isShapeFeedbackEditable;
  }

  private getAddIssueOption() {
    const point = this.store.state().mouse.imagePoint;
    return {
      label: 'Add issue',
      hidden: !this.shouldShowAddIssue(),
      action: () => {
        this.shellConnector.addClientFeedback(point);
      },
    };
  }

  private shouldShowAddIssue() {
    return (
      this.store.state().isClientFeedbacksEditable &&
      this.shellConnector.hasPermission('issue_create')
    );
  }
}
